import React from 'react';

import {CommonInputInterface, InputAppearance, InputShape, InputSize} from '../common-input-interface';
import {HelpIcon} from '../../svg-icon';
import {PopoverButton} from '../../popover-button';
import {cls} from '../../../utils';

import css from './field.module.css';

type Props = {
	className?: string;
	/* показывать лебл вверху (меняется с анимацией) */
	topLabel?: boolean;
	/* флаг что поле открыто (для повышения z-index) */
	isOpen?: boolean;
	/* пропсы для лейбла */
	labelProps?: React.LabelHTMLAttributes<HTMLLabelElement>;
	/* общие пропсы для контролов */
	commonProps: Pick<
		CommonInputInterface<any>,
		'label' | 'error' | 'helperText' | 'bottomText' | 'disabled' | 'noButtons'
	>;
	/* внутренняя ссылка, нужна для downshift, например */
	innerRef?: React.Ref<HTMLDivElement>;
	outerRef?: React.Ref<HTMLDivElement>;
	normalLabel?: boolean;
	buttons?: React.ReactNode;
	/** иконка или другой элемент перед полем ввода внутри рамки */
	contentBefore?: React.ReactNode;
	/** класс самой рамки поля, без подписи и подсказок */
	boxClassName?: string;
	/** рамка акцентного цвета: поле ждёт, пока в нём что-то выберут */
	highlighted?: boolean;
	hasFocus?: boolean;
	/* вид, размер и форма поля — как у кнопки */
	appearance?: InputAppearance;
	size?: InputSize;
	shape?: InputShape;
	inputId?: string;
};

/* размер подписи повторяет размер поля, но живёт на самой подписи — она вне рамки */
const LABEL_SIZE_CLASS = {small: 'labelSmall', medium: 'labelMedium', large: 'labelLarge'} as const;

export const Field: React.FC<Props & Omit<React.InputHTMLAttributes<HTMLDivElement>, keyof Props>> = (props) => {
	const {
		commonProps,
		className,
		normalLabel,
		labelProps,
		topLabel,
		children,
		innerRef,
		outerRef,
		isOpen,
		hasFocus,
		buttons,
		contentBefore,
		boxClassName,
		highlighted,
		appearance,
		size = 'medium',
		shape = 'rounded',
		inputId,
		...divProps
	} = props;
	const {error, label, disabled, helperText, bottomText, noButtons} = commonProps;
	/*
	 * Рамка, высота и отступы появляются только у полей ввода — они передают appearance.
	 * Чекбокс, переключатель, ползунок и составные контролы зовут Field без вида: им нужна
	 * только обвязка с подписью, ошибкой и подсказкой.
	 */
	/* у underline подпись плавает внутри поля, у outline стоит отдельной строкой сверху */
	const underline = appearance === 'underline';
	return (
		<div
			{...divProps}
			ref={outerRef}
			className={cls(className, css.field, !topLabel && css.labelToPlaceholder, isOpen && css.open)}
		>
			{!underline && !!label && (
				<label
					{...labelProps}
					id={inputId ? inputId + '-label' : labelProps?.id}
					htmlFor={inputId}
					className={cls(css.blockLabel, css[LABEL_SIZE_CLASS[size]], labelProps && labelProps.className)}
				>
					{label}
				</label>
			)}
			<div className={css.fieldWrapper}>
				<div
					className={cls(
						css.inputWrapper,
						!appearance && css.bare,
						appearance && css[appearance],
						appearance && css[size],
						appearance && shape !== 'rounded' && css[shape],
						disabled && css.disabled,
						!!error && error !== true && css.hasError,
						hasFocus && css.hasFocus,
						highlighted && css.highlighted,
						boxClassName,
					)}
					ref={innerRef}
				>
					{!!contentBefore && <div className={css.contentBefore}>{contentBefore}</div>}
					<div className={css.input}>
						{!!label && underline && (
							<label
								{...labelProps}
								id={inputId ? inputId + '-label' : labelProps?.id}
								className={cls(
									!normalLabel && css.label,
									normalLabel && css.normalLabel,
									labelProps && labelProps.className,
								)}
							>
								{label}
							</label>
						)}
						{children}
					</div>
					{buttons && !noButtons && (
						<div className={cls(css.actionButtons, underline && css.underline)}>{buttons}</div>
					)}
				</div>

				{!!helperText && (
					<div className={css.noteWrapper}>
						<PopoverButton
							appearance="subtle"
							icon={<HelpIcon />}
							popover={<div className={css.note}>{helperText}</div>}
							className={css.noteButton}
							message
						/>
					</div>
				)}
			</div>
			{!!error && error !== true && <div className={css.error}>{error}</div>}
			{!!bottomText && <div className={css.bottomText}>{bottomText}</div>}
		</div>
	);
};
